import * as React from 'react';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import {
  ProjectPickerActions,
  IApplicationState,
  CLEAR_EXPERIMENT_LIST,
} from '@store';

import { useDispatch, useSelector } from 'react-redux';

import { IAccount } from '@models/accounts';

export interface IAccountPickerStateProps {
  accountId?: string;
  accounts?: IAccount[];
}

export interface IAccountPickerDispatchProps {
  selectAccountId: (accountId: string) => void;
}

interface ISelectedAccountFormat {
  value: string;
  key: string;
  label: string;
}

const AccountPicker: React.FC<
  IAccountPickerStateProps & IAccountPickerDispatchProps
> = ({ selectAccountId, accountId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [accountsDisplay, setAccountsDisplay] = React.useState<
    IAccount[] | null
  >([]);

  const [accountPickerOptions, setaccountPickerOptions] = React.useState<
    ISelectedAccountFormat[] | []
  >([]);

  const [selectedAccountOption, setSelectedAccountOption] =
    React.useState<ISelectedAccountFormat | null>(null);

  // get the activeAccountId project in the store
  const activeAccountId = useSelector(
    (state: IApplicationState) => state.projectPicker.activeAccountId
  );

  // subcribe to the account projects in the store
  const accountsList = useSelector(
    (state: IApplicationState) => state.projectPicker.accountsList
  );

  const sortAlphabetically = (accounts: IAccount[]) => {
    const accountsSorted = accounts.sort((a, b) =>
      a.accountName.localeCompare(b.accountName)
    );
    return accountsSorted;
  };

  React.useEffect(() => {
    if (accountsList) {
      setAccountsDisplay(sortAlphabetically(accountsList));
    }
  }, [accountsList]);

  //
  React.useEffect(() => {
    if (activeAccountId && accountPickerOptions.length > 0) {
      const foundOption = accountPickerOptions.filter(
        (options) => options.key === activeAccountId
      )[0];
      if (foundOption) {
        setSelectedAccountOption(foundOption);
      }
    }
  }, [activeAccountId, accountPickerOptions]);

  React.useEffect(() => {
    if (accountsDisplay) {
      // return display format for AcocuntPicker
      const options: ISelectedAccountFormat[] = accountsDisplay.map(
        ({ accountId, accountName }: IAccount): ISelectedAccountFormat => {
          return {
            value: accountId,
            key: accountId,
            label: accountName,
          };
        }
      );

      setaccountPickerOptions(options);
    }
  }, [accountsDisplay]);

  const onSelectionHandler = React.useCallback(
    (selectedAcount) => {
      if (selectedAcount) {
        setSelectedAccountOption(selectedAcount);
        dispatch({ type: CLEAR_EXPERIMENT_LIST });
        // couldn't find the project in history
        dispatch(ProjectPickerActions.updateAccountId(selectedAcount.key));
      }
    },
    [activeAccountId]
  );

  return (
    <div>
      {activeAccountId &&
      accountPickerOptions.length > 0 &&
      selectedAccountOption ? (
        <Select
          options={accountPickerOptions}
          value={selectedAccountOption}
          onChange={(e) => onSelectionHandler(e)}
          placeholder='-'
          noOptionsMessage={() => t('accountPicker_notFound')}
          className='account-picker__container'
          classNamePrefix='account-picker'
        />
      ) : null}
    </div>
  );
};

export default AccountPicker;
